import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("artist");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(name, email, password, role);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1748466694158-3e172aac0968?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
            <span className="text-zinc-50 font-bold text-xl">CG</span>
          </div>
          <h1 className="text-4xl font-bold text-zinc-50 font-['Manrope'] mb-4">
            Join the Team
          </h1>
          <p className="text-zinc-400 text-lg max-w-md">
            Create your account to start collaborating on stunning CGI projects.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-zinc-50 font-bold">CG</span>
            </div>
            <span className="text-xl font-semibold text-zinc-50 font-['Manrope']">CGI Portal</span>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-zinc-50 font-['Manrope'] mb-2">Create account</h2>
            <p className="text-zinc-500">Fill in your details to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 focus:ring-orange-500"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 focus:ring-orange-500"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 focus:ring-orange-500 pr-10"
                  data-testid="register-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 text-zinc-500 hover:text-zinc-50"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-zinc-300">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  className="bg-zinc-900 border-zinc-800 text-zinc-50 focus:border-orange-500 focus:ring-orange-500"
                  data-testid="register-role"
                >
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="admin" className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                    Admin
                  </SelectItem>
                  <SelectItem value="team" className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                    Team Member
                  </SelectItem>
                  <SelectItem value="artist" className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                    CGI Artist
                  </SelectItem>
                  <SelectItem value="client" className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                    Client
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white glow-orange"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-zinc-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-orange-500 hover:text-orange-400 font-medium"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
